import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { api, fmtDate } from '../lib/api.js';
import { BatchPLSummary, BatchTransactions, BatchDistributionPreview } from '../components/BatchDrillIn.jsx';

const EVENT_TYPES = ['weight', 'vaccination', 'breeding', 'pregnancy_check', 'calving', 'health_treatment', 'sale', 'death', 'note'];

export default function CattleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isOwner } = useOutletContext();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  async function toggleHidden() {
    const next = data.animal.disposition === 'active' ? 'culled' : 'active';
    try { await api.patch(`/api/cattle/${id}`, { disposition: next }); load(); }
    catch (e) { setErr(e.message); }
  }
  async function deleteAnimal() {
    const a = data.animal;
    const label = `cattle ${a.name || a.ear_tag || `#${a.id}`}`;
    if (!confirm(
      `Delete ${label}?\n\n` +
      `• All events for this animal will be deleted.\n` +
      `• Offspring records that listed this animal as dam/sire stay, but the dam/sire field becomes empty.\n` +
      `• Income / expense rows tagged to this animal stay, but un-tagged (they roll up to the cattle segment).\n` +
      `• If you only want to declutter, click Hide (marks as culled).\n\n` +
      `Type "delete" in the next prompt to confirm.`,
    )) return;
    const typed = prompt(`Confirm: type "delete" exactly to remove ${label}`);
    if (typed !== 'delete') return;
    try {
      const r = await api.delete(`/api/cattle/${id}`);
      const u = r.untagged || {};
      alert(`✓ Deleted. Un-tagged ${u.income || 0} income row(s), ${u.expense_lines || 0} expense line(s), removed ${u.events || 0} event(s), cleared dam on ${u.offspring_dam || 0} and sire on ${u.offspring_sire || 0} offspring.`);
      navigate('/cattle');
    } catch (e) { setErr(e.message); }
  }
  const [eventForm, setEventForm] = useState({
    event_type: 'weight',
    date: new Date().toISOString().slice(0, 10),
    notes: '',
  });
  const [busy, setBusy] = useState(false);

  async function load() {
    try { setData(await api.get(`/api/cattle/${id}`)); } catch (e) { setErr(e.message); }
  }
  useEffect(() => { load(); }, [id]);

  if (err) return <div className="text-rust-700 text-sm">{err}</div>;
  if (!data) return <div className="text-ink-700/60 italic">Loading…</div>;
  const a = data.animal;

  async function addEvent(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/api/cattle/${id}/events`, {
        event_type: eventForm.event_type,
        date: eventForm.date,
        notes: eventForm.notes.trim() || null,
      });
      setEventForm(f => ({ ...f, notes: '' }));
      load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <Link to="/cattle" className="text-sm text-olive-700 hover:underline">‹ Cattle</Link>

      <header className="card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-serif text-olive-900">
              {a.name || a.ear_tag || `#${a.id}`}
              {a.adca_reg_no && <span className="ml-3 text-sm font-sans text-ink-700/60">ADCA {a.adca_reg_no}</span>}
            </h1>
            <div className="text-sm text-ink-700/70 mt-1 capitalize">
              {a.sex} {a.color && <>· {a.color}</>} {a.dob && <>· DOB {fmtDate(a.dob)}</>}
              {a.disposition !== 'active' && <span className="ml-2 pill bg-ink-700/10 text-ink-700">{a.disposition}{a.disposition_date && ` · ${fmtDate(a.disposition_date)}`}</span>}
            </div>
          </div>
          {isOwner && (
            <div className="flex gap-2 text-xs">
              <button onClick={toggleHidden} className="btn-ghost text-xs py-1 px-2">
                {a.disposition === 'active' ? 'Hide' : 'Unhide'}
              </button>
              <button onClick={deleteAnimal} className="text-xs text-rust-700 hover:underline px-2">
                Delete
              </button>
            </div>
          )}
        </div>
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 mt-4 text-sm">
          <Field label="Polled">{a.polled_status || '—'}</Field>
          <Field label="A2/A2">{a.a2_status || '—'}</Field>
          <Field label="PHA">{a.pha_status || '—'}</Field>
          <Field label="Chondro">{a.chondro_status || '—'}</Field>
          <Field label="Dam">{a.dam_name ? <Link to={`/cattle/${a.dam_id}`} className="text-olive-700 hover:underline">{a.dam_name}</Link> : '—'}</Field>
          <Field label="Sire">{a.sire_name ? <Link to={`/cattle/${a.sire_id}`} className="text-olive-700 hover:underline">{a.sire_name}</Link> : '—'}</Field>
          <Field label="Ear tag">{a.ear_tag || '—'}</Field>
          <Field label="Events">{data.events.length}</Field>
        </dl>
        {a.notes && <div className="mt-3 text-sm text-ink-700/80 italic">{a.notes}</div>}
      </header>

      {isOwner && (
        <div className="flex flex-wrap gap-2">
          <button
            className="btn-primary text-sm"
            onClick={() => navigate(`/income/new?segment=cattle&cattle_id=${a.id}`)}>
            + Log income for this animal
          </button>
          <button
            className="btn-ghost text-sm"
            onClick={() => navigate(`/receipts/new?segment=cattle&cattle_id=${a.id}`)}>
            + Log receipt for this animal
          </button>
        </div>
      )}

      <BatchPLSummary  scope={{ cattle_id: a.id }} segmentId="cattle" />
      <BatchTransactions scope={{ cattle_id: a.id }} isOwner={isOwner} />
      {isOwner && (
        <BatchDistributionPreview scope={{ cattle_id: a.id }} segmentId="cattle" />
      )}

      {data.offspring.length > 0 && (
        <section className="card">
          <h2 className="font-medium text-olive-900 mb-2">Offspring ({data.offspring.length})</h2>
          <ul className="space-y-1 text-sm">
            {data.offspring.map(o => (
              <li key={o.id}>
                <Link to={`/cattle/${o.id}`} className="text-olive-700 hover:underline">
                  {o.name || o.ear_tag || `#${o.id}`}
                </Link>
                <span className="text-ink-700/60 ml-2 capitalize">{o.sex}</span>
                {o.dob && <span className="text-ink-700/50 ml-2">{fmtDate(o.dob)}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="card space-y-3">
        <h2 className="font-medium text-olive-900">Add event</h2>
        <form onSubmit={addEvent} className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-4">
            <label className="label">Type</label>
            <select className="input" value={eventForm.event_type} onChange={e => setEventForm(f => ({ ...f, event_type: e.target.value }))}>
              {EVENT_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
            </select>
          </div>
          <div className="col-span-3">
            <label className="label">Date</label>
            <input className="input" type="date" value={eventForm.date} onChange={e => setEventForm(f => ({ ...f, date: e.target.value }))} />
          </div>
          <div className="col-span-5">
            <label className="label">Notes</label>
            <input className="input" value={eventForm.notes} onChange={e => setEventForm(f => ({ ...f, notes: e.target.value }))} placeholder='e.g. "740 lbs", "bred to Big Bull on 2026-04-01"' />
          </div>
          <div className="col-span-12 flex justify-end">
            <button className="btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Add event'}</button>
          </div>
        </form>
      </section>

      <section className="card">
        <h2 className="font-medium text-olive-900 mb-3">Event history</h2>
        {data.events.length === 0 ? (
          <p className="text-sm text-ink-700/60 italic">No events yet.</p>
        ) : (
          <ul className="space-y-2">
            {data.events.map(ev => (
              <li key={ev.id} className="border-l-2 border-olive-100 pl-3 py-1">
                <div className="text-sm">
                  <span className="pill bg-olive-100 text-olive-900 mr-2">{ev.event_type.replace('_', ' ')}</span>
                  <span className="text-ink-900">{ev.notes || '—'}</span>
                </div>
                <div className="text-xs text-ink-700/60 mt-0.5">
                  {fmtDate(ev.date)}
                  {ev.user_name && <> · by {ev.user_name}</>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-ink-700/60">{label}</dt>
      <dd className="text-ink-900 mt-0.5">{children}</dd>
    </div>
  );
}
